// src/masterLayout/MasterLayout.jsx
import { useEffect, useState, useMemo } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { useAuth } from "../context/AuthContext";
import { SIDEBAR_MENU } from "../constants/sidebarMenu";
import { ROLE_LABELS } from "../constants/roles";
import { useCompany } from '../context/CompanyContext';
import CompanySelector from '../components/CompanySelector/CompanySelector';
import { useNotifications } from '../context/NotificationContext';
import api from '../services/api';
import './MasterLayout.css';

const MasterLayout = ({ children }) => {
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, canAccessMenuItem, logout, isAuthenticated, isSuperAdmin } = useAuth();
  
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const NOTIF_ICON = {
  ORDER:    'iconoir:cart',
  PAYMENT:  'iconoir:wallet',
  GODOWN:   'iconoir:home-warehouse', // fallback fine if not in your icon set
  TARGET:   'iconoir:target',
  BIRTHDAY: 'iconoir:birthday-cake',
  SYSTEM:   'iconoir:bell',
};
const [selectedCompanyId, setSelectedCompanyId] = useState(
  () => localStorage.getItem('selectedCompanyId') || ''
);
const { selectedCompany, companies } = useCompany();
const [companiesCache, setCompaniesCache] = useState({});


const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const handleNotifClick = (n) => {
  if (!n.isRead) markRead(n.id);
  if (n.link) navigate(n.link);
};



// ✅ Watch for company switch in navbar
useEffect(() => {
  const t = setInterval(() => {
    const cid = localStorage.getItem('selectedCompanyId') || '';
    if (cid !== selectedCompanyId) setSelectedCompanyId(cid);
  }, 1000);
  return () => clearInterval(t);
}, [selectedCompanyId]);

// ✅ For SA/OA — fetch company name if not cached
useEffect(() => {
  const isAdmin = user?.role?.name === 'SUPER_ADMIN' || user?.role?.name === 'OFFICE_ADMIN';
  if (!isAdmin || !selectedCompanyId || companiesCache[selectedCompanyId]) return;
  
  api.get(`/companies/${selectedCompanyId}`)
    .then(res => {
      const name = res.data?.data?.name || res.data?.name || `Company #${selectedCompanyId}`;
      setCompaniesCache(prev => ({ ...prev, [selectedCompanyId]: name }));
    })
    .catch(() => {
      setCompaniesCache(prev => ({ ...prev, [selectedCompanyId]: `Company #${selectedCompanyId}` }));
    });
}, [user, selectedCompanyId, companiesCache]);

const COMPANY_MAP = {
  6 : 'Amicus Agri Solutions',
  1: 'Microbax India Limited',  
  5: 'Viyoni Agro Chemicals (India) Pvt Ltd',
};

const resolvedCompanyName = (() => {
  const roleName = user?.role?.name;
  const isAdmin  = roleName === 'SUPER_ADMIN' || roleName === 'OFFICE_ADMIN';
  
  if (isAdmin) {
    const cid = selectedCompany?.id || localStorage.getItem('selectedCompanyId');
    if (!cid) return 'No company selected';
    return COMPANY_MAP[cid] || `Company ID ${cid}`;
  }
  return user?.company?.name || '';
})();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/sign-in', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Auto-close mobile sidebar whenever the route changes
  useEffect(() => {
    setMobileMenu(false);
  }, [location.pathname]);

  const filteredMenu = useMemo(() => {
    if (!user) return [];
    return SIDEBAR_MENU.filter((menuItem) => {
      if (!canAccessMenuItem(menuItem)) return false;
      if (menuItem.children) {
        const accessibleChildren = menuItem.children.filter((child) => canAccessMenuItem(child));
        return accessibleChildren.length > 0;
      }
      return true;
    }).map((menuItem) => {
      if (menuItem.children) {
        return { ...menuItem, children: menuItem.children.filter((child) => canAccessMenuItem(child)) };
      }
      return menuItem;
    });
  }, [user, canAccessMenuItem]);

  useEffect(() => {
    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");
      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) submenu.style.maxHeight = "0px";
      });

      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) submenu.style.maxHeight = `${submenu.scrollHeight}px`;
      }
    };

    const dropdownTriggers = document.querySelectorAll(".sidebar-menu .dropdown > a");
    dropdownTriggers.forEach((trigger) => trigger.addEventListener("click", handleDropdownClick));

    const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
    allDropdowns.forEach((dropdown) => {
      const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
      submenuLinks.forEach((link) => {
        if (
          link.getAttribute("href") === location.pathname ||
          link.getAttribute("to") === location.pathname
        ) {
          dropdown.classList.add("open");
          const submenu = dropdown.querySelector(".sidebar-submenu");
          if (submenu) submenu.style.maxHeight = `${submenu.scrollHeight}px`;
        }
      });
    });

    return () => {
      dropdownTriggers.forEach((trigger) => trigger.removeEventListener("click", handleDropdownClick));
    };
  }, [location.pathname, filteredMenu]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

 const renderMenuItem = (menuItem) => {
  const hasChildren = menuItem.children?.length > 0;

  if (hasChildren) {
    return (
      <li key={menuItem.id} className="dropdown">
        <Link to="#">
          <Icon icon={menuItem.icon} className="menu-icon" />
          <span>{menuItem.title}</span>
        </Link>
        <ul className="sidebar-submenu">
          {menuItem.children.map((child) => (
            <li key={child.id || child.path}>
              <NavLink
                to={child.path}
                className={({ isActive }) => (isActive ? "active-page" : "")}
              >
                <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />
                {child.showCompany ? (
                  <span className="d-inline-flex flex-column" style={{ lineHeight: 1.15 }}>
                    <span>{child.title}</span>
                    <span
                      className="text-secondary-light"
                      style={{
                        fontSize: 11,
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 180,
                      }}
                    >
                      {resolvedCompanyName}
                    </span>
                  </span>
                ) : (
                  child.title
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li key={menuItem.id}>
      <NavLink
        to={menuItem.path}
        className={({ isActive }) => (isActive ? "active-page" : "")}
      >
        <Icon icon={menuItem.icon} className="menu-icon" />
        <span>{menuItem.title}</span>
      </NavLink>
    </li>
  );
};

  if (!user) return null;

  return (
    <section className={mobileMenu ? "overlay active" : "overlay"}>
      <aside
        className={
          sidebarActive ? "sidebar active" : mobileMenu ? "sidebar sidebar-open" : "sidebar"
        }
      >
        <button
          onClick={() => setMobileMenu(!mobileMenu)}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to="/" className="sidebar-logo">
            <img src="/assets/images/logo.png" alt="site logo" className="light-logo" />
            <img src="/assets/images/logo.png" alt="site logo" className="dark-logo" />
            <img src="/assets/images/logo.png" alt="site logo" className="logo-icon" />
          </Link>
        </div>

        {user && (
          <div className="px-16 py-8 mb-8">
            <div className="bg-primary-50 rounded-8 px-12 py-8 text-center">
              <span className="text-sm text-primary-600 fw-medium">
                {user.roleDisplayName || ROLE_LABELS[user.role] || user.role}
              </span>
            </div>
          </div>
        )}

        <div className="sidebar-menu-area">
          <ul className="sidebar-menu" id="sidebar-menu">
            {filteredMenu.map(renderMenuItem)}
          </ul>
        </div>
      </aside>

      <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>

        {/* ── Navbar ───────────────────────────────────────────── */}
        <div className="navbar-header">
          <div className="navbar-inner">

            {/* LEFT: hamburgers + search */}
            <div className="navbar-left">
              {/* Desktop sidebar toggle */}
              <button
                type="button"
                className="sidebar-toggle"
                onClick={() => setSidebarActive(!sidebarActive)}
              >
                {sidebarActive ? (
                  <Icon icon="iconoir:arrow-right" className="icon text-2xl non-active" />
                ) : (
                  <Icon icon="heroicons:bars-3-solid" className="icon text-2xl non-active" />
                )}
              </button>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenu(!mobileMenu)}
                type="button"
                className="sidebar-mobile-toggle"
              >
                <Icon icon="heroicons:bars-3-solid" className="icon" />
              </button>

              {/* Search — hidden on small mobile, visible from sm up */}
              <form className="navbar-search navbar-search--desktop">
                <input type="text" name="search" placeholder="Search" />
                <Icon icon="ion:search-outline" className="icon" />
              </form>
            </div>

            {/* RIGHT: theme + company selector + badge + bell + avatar */}
            <div className="navbar-right">
              <ThemeToggleButton />

              {/* Company selector — only for super admin */}
              {isSuperAdmin && (
                <div className="navbar-company-selector">
                  <CompanySelector />
                </div>
              )}

              {/* Selected company badge — hidden on very small screens */}
              {isSuperAdmin && selectedCompany && (
                <span className="navbar-company-badge">
                  {selectedCompany.name}
                </span>
              )}

              {/* Bell */}
              {/* Bell */}
<div className="dropdown">
  <button className="navbar-icon-btn" type="button" data-bs-toggle="dropdown">
    <Icon icon="iconoir:bell" className="text-primary-light text-xl" />
    {unreadCount > 0 && (
      <span
        className="position-absolute"
        style={{
          top: 2, right: 2, minWidth: 18, height: 18, padding: '0 5px',
          background: '#e53935', color: '#fff', borderRadius: 9,
          fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
        }}
      >
        {unreadCount > 99 ? '99+' : unreadCount}
      </span>
    )}
  </button>

  <div className="dropdown-menu to-top dropdown-menu-lg p-0">
    <div className="m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
      <h6 className="text-lg text-primary-light fw-semibold mb-0">Notifications</h6>
      <div className="d-flex align-items-center gap-2">
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="border-0 bg-transparent text-primary-600 fw-medium text-sm"
            style={{ cursor: 'pointer' }}
          >
            Mark all read
          </button>
        )}
        <span className="text-primary-600 fw-semibold text-md w-32-px h-32-px rounded-circle bg-base d-flex justify-content-center align-items-center">
          {unreadCount}
        </span>
      </div>
    </div>

    <div className="max-h-400-px overflow-y-auto scroll-sm pe-4">
      {notifications.length === 0 ? (
        <p className="text-center text-secondary-light py-3">No notifications</p>
      ) : (
        notifications.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => handleNotifClick(n)}
            className="w-100 border-0 bg-transparent text-start px-16 py-12 d-flex gap-3 align-items-start notif-item"
            style={{
              background: n.isRead ? 'transparent' : 'rgba(72,187,120,0.08)',
              borderBottom: '1px solid #f1f5f9',
              cursor: 'pointer',
            }}
          >
            <span
              className="w-40-px h-40-px rounded-circle d-flex justify-content-center align-items-center flex-shrink-0"
              style={{ background: '#e8f5e9' }}
            >
              <Icon icon={NOTIF_ICON[n.type] || 'iconoir:bell'} className="text-primary-600 text-lg" />
            </span>
            <span className="flex-grow-1" style={{ minWidth: 0 }}>
              <span className="d-block fw-semibold text-primary-light text-sm">{n.title}</span>
              <span className="d-block text-secondary-light text-sm" style={{ wordBreak: 'break-word' }}>
                {n.message}
              </span>
              <span className="d-block text-secondary-light" style={{ fontSize: 11, marginTop: 2 }}>
                {timeAgo(n.createdAt)}
              </span>
            </span>
            {!n.isRead && (
              <span className="flex-shrink-0" style={{
                width: 8, height: 8, borderRadius: '50%', background: '#e53935', marginTop: 6,
              }} />
            )}
          </button>
        ))
      )}
    </div>
  </div>
</div>

              {/* Avatar / user dropdown */}
              <div className="dropdown">
               <button
  className="navbar-avatar-btn"
  type="button"
  data-bs-toggle="dropdown"
>
  {(() => {
    const initials = (
      `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase()
    ) || user?.username?.[0]?.toUpperCase() || '?';

    return user?.avatar
      ? <img src={user.avatar} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} alt={initials} />
      : <div style={{
          width: 36, height: 36, borderRadius: '50%', background: '#48bb78',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0,
        }}>
          {initials}
        </div>;
  })()}  
</button>
                <div className="dropdown-menu to-top dropdown-menu-sm">
                  <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                    <div>
                      <h6 className="text-lg text-primary-light fw-semibold mb-4">
                        {user?.name || user?.username}
                      </h6>
                      <span className="text-secondary-light fw-medium text-sm">
                        {user?.roleDisplayName || ROLE_LABELS[user?.role]}
                      </span>
                    </div>
                  </div>
                  <ul className="to-top-list">
                    <li>
                      <Link
                        className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
                        to="/view-profile"
                      >
                        <Icon icon="solar:user-linear" className="icon text-xl" /> My Profile
                      </Link>
                    </li>
                    <li>
                      <button
                        className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3 w-100 border-0 bg-transparent"
                        onClick={handleLogout}
                      >
                        <Icon icon="lucide:power" className="icon text-xl" /> Log Out
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            {/* END RIGHT */}

          </div>

          {/* Mobile search row — shown only on small screens */}
          <div className="navbar-search-mobile">
            <form className="navbar-search navbar-search--mobile">
              <input type="text" name="search" placeholder="Search" />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>
        </div>
        {/* ── End Navbar ───────────────────────────────────────── */}

        <div className="dashboard-main-body">{children}</div>

        <footer className="d-footer">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <p className="mb-0">© 2024. All Rights Reserved.</p>
            </div>
            <div className="col-auto">
              <p className="mb-0">
                Made by <span className="text-primary-600">Haritpane Agro</span>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;